import axiosInstance from "@/lib/axios";
import { switchBackPath, switchUserPath } from "@/config/endpoints";
import {
  ACCESS_TOKEN_KEY,
  CURRENT_USER_KEY,
  ROUTE_IDENTITY_KEY,
  readActorSession,
  restoreActorLocally,
  writeActorSession,
} from "./impersonationStorage";

/**
 * "Switch user" (view-as) — the two transitions.
 *
 * Both end in a FULL RELOAD on purpose. A session swap has to reset every
 * layer that caches identity: the Redux slices (~50, some redux-persisted),
 * the React Query cache, the socket connection (rooms are per user), the
 * leads-prefetch guard, the tab-local route identity stamp… Login and logout
 * reset those piecemeal and have a history of leaks between sessions
 * (see the logout handler's comments). Reloading on a fresh token is the one
 * path that is provably clean, and a switch is rare enough to afford it.
 */

function unwrapStoredUser(raw: string | null): { id: number; name: string | null; email: string | null } | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const user = parsed?.user !== undefined ? parsed.user : parsed;
    const id = Number(user?.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      id,
      name: typeof user?.name === "string" ? user.name : null,
      email: typeof user?.email === "string" ? user.email : null,
    };
  } catch {
    return null;
  }
}

async function purgeAndReload(): Promise<void> {
  try {
    // Stop redux-persist writing the OUTGOING session back after the purge.
    const { persistor } = await import("@/stores/store");
    persistor.pause();
    await persistor.purge();
  } catch {
    /* best effort — the reload hydrates from the new token regardless */
  }
  try {
    sessionStorage.removeItem(ROUTE_IDENTITY_KEY);
  } catch {
    /* storage disabled */
  }
  // `replace` so Back can't return to a URL the other identity can't open.
  window.location.replace("/");
}

/**
 * Become `candidate`. Resolves only if the reload is under way; throws with
 * the server's message when the switch is refused (403: not under you / not
 * in your branches / already viewing as someone).
 */
export async function switchToUser(candidate: {
  id: number;
  name: string;
  role?: { name: string } | null;
}): Promise<void> {
  const res = await axiosInstance.post(`${switchUserPath}/${candidate.id}`);
  const accessToken: unknown = res.data?.accessToken;
  const user = res.data?.user;
  if (typeof accessToken !== "string" || !user?.id) {
    throw new Error("The switch did not return a session.");
  }

  const actorToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const actorRaw = localStorage.getItem(CURRENT_USER_KEY);
  const actorUser = unwrapStoredUser(actorRaw);
  if (!actorToken || !actorUser) {
    throw new Error("Your own session could not be saved for the switch back.");
  }

  writeActorSession({
    token: actorToken,
    currentUserRaw: actorRaw,
    user: actorUser,
    viewing: {
      id: Number(user.id),
      name: String(user.name ?? candidate.name),
      roleName: user.role?.name ?? candidate.role?.name ?? null,
    },
    startedAt: new Date().toISOString(),
  });
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  // Same `{ user }` wrapper the login/verify path stores, so the RBAC slice
  // hydrates the viewed person's permissions on the very first render.
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify({ user }));

  await purgeAndReload();
}

/**
 * Back to the manager's own account. `audit` posts the switch-back event with
 * the view-as token first (best effort); pass `false` when that token is
 * already dead (verify failed) — the restore itself never needs the server.
 */
export async function switchBackToActor(opts?: { audit?: boolean }): Promise<boolean> {
  const actor = readActorSession();
  if (!actor) return false;
  if (opts?.audit !== false) {
    try {
      await axiosInstance.post(switchBackPath);
    } catch {
      /* audit only — never block the switch back */
    }
  }
  restoreActorLocally(actor);
  await purgeAndReload();
  return true;
}
